/**
 * The English catalogue — the official language, and the only complete one.
 *
 * This is the key list. Every other locale is a `Partial<Catalog>` checked
 * against it by TypeScript, so a typo in a translation file is a build error
 * rather than a screen that renders `wallet.blance`. `npm run i18n:audit`
 * checks the other direction: keys that exist here and nowhere else, keys used
 * in code that exist nowhere, and text still hardcoded in a component.
 *
 * Conventions:
 *
 *  - Keys are `namespace.name`, namespaced by screen or by concern, so a
 *    translator can work through one surface at a time.
 *  - `{placeholders}` are named, never positional — a translator reordering a
 *    sentence must not have to track which `%s` was which.
 *  - A key with `_one`/`_few`/`_many`/`_other` variants is read through
 *    `plural()`, which asks `Intl.PluralRules` for the right one. Russian and
 *    Arabic both need more than two forms; English declaring only `_one` and
 *    `_other` does not stop them from having theirs.
 *  - `error.*` keys are the application's error codes verbatim. The server
 *    sends English prose *and* a machine code; the client prefers its own
 *    localised copy for the code and falls back to the server's sentence for a
 *    code it does not know. That is how a backend message ends up translated
 *    without the backend having to hold six catalogues (§20).
 */
export const EN = {
  //---------------------------------------------------------------------------
  // Common
  //---------------------------------------------------------------------------
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.save': 'Save',
  'common.back': 'Back',
  'common.retry': 'Try again',
  'common.reload': 'Reload',
  'common.dismiss': 'Dismiss',
  'common.loading': 'Loading…',
  'withdraw.belowMinimumBody': 'Withdrawals start at {min} Gems — you are {gap} Gems away.',
  'withdraw.amountToWithdraw': 'Amount to withdraw',
  'withdraw.availableAmount': 'Available: {amount} {coin}',
  'common.coinBalance': '{coin} balance',
  'common.minShort': 'Min {amount}',
  'swap.serverNote': 'The rate and the amount you receive are set by the server.',
  'deposit.sentIt': 'I have sent it',
  'deposit.creditedAfter': 'Deposits are credited after the transfer is confirmed on the blockchain.',
  'deposit.notOpen': 'Deposits are not open yet. When they are, the address to send to appears here.',
  'deposit.amountSent': 'Amount sent ({asset})',
  'activity.loadMore': 'Load more',
  'profile.minerSince': 'Miner since {date}',
  'profile.idLabel': 'ID: {id}',
  'minerPanel.working': 'Working…',
  'minerPanel.workingFor': 'Working —',
  'minerPanel.timeLeft': 'Left:',
  'time.shiftLength': '{length} shift',
  'notice.kicker.error': 'Error',
  'notice.kicker.denied': 'Denied',
  'notice.kicker.banned': 'Banned',
  'notice.kicker.done': 'Done',
  'notice.kicker.reward': 'Reward',
  'notice.tryAgainSoon': 'Try again in a moment',
  'notice.addedToBalance': 'Added to your balance!',
  'levelUp.title': 'Level up!',
  'levelUp.level': 'Level {level}',
  'levelUp.next': 'Next level at {amount} Gems earned',
  'levelUp.max': 'You are at the top level',
  'levelUp.continue': 'Continue',
  'mining.claimedAllTitle': '+{amount} Gems claimed',
  'invite.friendWord_one': 'Friend',
  'invite.friendWord_other': 'Friends',
  'mining.claimedAllBody_one': '{count} shift collected.',
  'mining.claimedAllBody_other': '{count} shifts collected.',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.paste': 'Paste',
  'common.max': 'Max',
  'common.clear': 'Clear',
  'common.next': 'Next page',
  'common.previous': 'Previous page',
  'common.gems': 'Gems',
  'common.network': 'Network',
  'common.amount': 'Amount',
  'common.status': 'Status',
  'common.somethingWrong': 'Something went wrong',
  'common.noConnection': 'No connection',
  'common.noConnectionHint': 'Check your connection and try again.',

  //---------------------------------------------------------------------------
  // Boot and fatal states
  //---------------------------------------------------------------------------
  'boot.opening': 'Opening the mine…',
  'boot.lighting': 'Lighting the lamps…',
  'boot.artwork': 'Loading the artwork',
  'boot.signInFailed': 'We could not sign you in.',
  'boot.crashTitle': 'Something broke on this screen',
  'boot.crashBody': 'Reopening the app should fix it. If it keeps happening, tell support.',
  'boot.notFoundTitle': 'Page not found',
  'boot.notFoundBody': 'That screen does not exist in CraftGem.',
  'boot.notFoundCta': 'Back to the mine',

  //---------------------------------------------------------------------------
  // Navigation
  //---------------------------------------------------------------------------
  'nav.main': 'Main',
  'nav.home': 'Home',
  'nav.mining': 'Mining',
  'nav.tasks': 'Tasks',
  'nav.wallet': 'Wallet',
  'nav.profile': 'Profile',
  'nav.invite': 'Invite friends',

  //---------------------------------------------------------------------------
  // Home
  //---------------------------------------------------------------------------
  'home.availableBalance': 'Available balance',
  'home.rateUnit': 'CraftGem / hour',
  'home.enterMine': 'Enter the mine',
  'home.dailyCombo': 'Daily combo',
  'home.roulette': 'Roulette',
  'home.backToHome': 'Back to Home',

  //---------------------------------------------------------------------------
  // Miner states
  //---------------------------------------------------------------------------
  'miner.state.locked': 'Locked',
  'miner.state.inactive': 'Inactive',
  'miner.state.watching': 'Watching',
  'miner.state.verifying': 'Verifying',
  'miner.state.working': 'Working',
  'miner.state.finished': 'Finished',
  'miner.state.claim': 'Claim',
  'miner.state.error': 'Error',
  'miner.hint.locked': 'Meet the requirement above to bring this miner into the shaft.',
  'miner.hint.lockedAll': 'Meet the requirements above to bring this miner into the shaft.',
  'miner.hint.lockedAny': 'Meet any one of the requirements above to bring this miner into the shaft.',
  'miner.hint.inactive': 'Watch one ad to start a shift. Production begins the moment the shift opens.',
  'miner.hint.watching': 'Let it finish — the shift starts once it is verified.',
  'miner.hint.verifying': 'Confirming the ad before the shift opens.',
  'miner.hint.working': 'Producing Gems. The reward can be claimed when the shift ends.',
  'miner.hint.finished': 'Closing out the shift.',
  'miner.hint.claim': 'Collect it, then watch another ad to send this miner back down.',
  'miner.hint.error': 'Nothing was charged and no shift was started. You can try again.',

  'miner.headline.locked': 'Not on your crew yet',
  'miner.headline.inactive': 'Resting',
  'miner.headline.watching': 'Ad playing',
  'miner.headline.verifying': 'Verifying with the server',
  'miner.headline.working': 'On shift',
  'miner.headline.finished': 'Shift finished',
  'miner.headline.claim': 'Reward ready',
  'miner.headline.error': 'That did not go through',
  'miner.action.watchAd': 'Watch ad to activate',
  'miner.action.watchAdCount': 'Watch ad {current} of {total}',
  'miner.action.watching': 'Watching…',
  'miner.action.activating': 'Activating…',
  'miner.action.claimReward': 'Claim reward',
  'miner.action.tryAgain': 'Try again',

  //---------------------------------------------------------------------------
  // Miner panel
  //---------------------------------------------------------------------------
  'minerPanel.level': 'Level',
  'minerPanel.status': 'Status',
  'minerPanel.rate': 'Mining rate',
  'minerPanel.shiftLength': 'Shift length',
  'minerPanel.shiftReward': 'Shift reward',
  'minerPanel.todaysWork': "Today's work",
  'minerPanel.totalShifts': 'Total shifts',
  // What a locked miner asks for (MinerRequirements): one sentence per kind.
  'minerReq.title': 'Requirements',
  'minerReq.level': 'Reach level {level}',
  'minerReq.referrals_one': 'Invite {count} active friend',
  'minerReq.referrals_other': 'Invite {count} active friends',
  'minerReq.tasks_one': 'Complete {count} task',
  'minerReq.tasks_other': 'Complete {count} tasks',
  'minerReq.task': 'Complete “{title}”',
  'minerReq.shifts_one': 'Claim {count} shift',
  'minerReq.shifts_other': 'Claim {count} shifts',
  'minerReq.miners_one': 'Have {count} miner in your crew',
  'minerReq.miners_other': 'Have {count} miners in your crew',
  'minerReq.or': 'or',
  'minerReq.done': 'Done',
  'minerReq.unavailable': 'Not available yet',
  'minerPanel.adRequirement': 'Ad requirement',
  'minerPanel.adRequirementValue_one': '{count} rewarded ad',
  'minerPanel.adRequirementValue_other': '{count} rewarded ads',
  'minerPanel.currentShift': 'Current shift',
  'minerPanel.earnedSoFar': 'Earned so far',
  'minerPanel.shiftProgress': 'Shift progress',
  'minerPanel.watchAd': 'Watch ad to activate',
  'minerPanel.claiming': 'Claiming…',
  'minerPanel.claimAmount': 'Claim {amount} Gems',
  'minerPanel.closingShift': 'Closing shift…',

  //---------------------------------------------------------------------------
  // The mine
  //---------------------------------------------------------------------------
  'mine.crew': 'Crew',
  'mine.perHour': '/h',
  'mine.readyTapMiner': '{amount} ready — tap a miner',
  'mine.claimPill': 'Claim +{amount}',
  'mine.onShift': 'On shift',
  'mine.idle': 'Idle',
  'mine.claimAll': 'Claim all +{amount}',
  'mine.nothingToClaim': 'Nothing to claim',
  'mine.noShift': 'No shift',
  'mine.sceneLabel': 'The CraftGem mine',

  //---------------------------------------------------------------------------
  // Rewarded ads
  //---------------------------------------------------------------------------
  'ad.label': 'Rewarded ad',
  'ad.loading': 'Loading ad…',
  'ad.verifying': 'Verifying with the server…',
  'ad.watchFull': 'Watch the full ad to start the shift.',
  'ad.tapToCount': 'Tap the button inside the ad so it counts.',
  'ad.tapAndStay': 'Tap the button inside the ad and stay at least {seconds} s so it counts.',
  'ad.count': 'Ad {current} of {total}',
  'ad.activating': 'Activating {miner}',
  'ad.verifyingNote': 'Nothing counts until the server confirms the ad.',
  'ad.watchFullCounted': 'Watch it to the end. An ad that is closed or fails does not count.',
  'ad.counted': '{watched} of {total} ads watched',
  'ad.left_one': '{count} more ad and {miner} starts the shift.',
  'ad.left_other': '{count} more ads and {miner} starts the shift.',
  'ad.next': 'Next ad',
  'ad.nextIn': 'Next ad in {seconds}s',
  'ad.wait': 'Wait {seconds}s',
  'ad.mock': 'Simulated ad: the server is not set up for Adsgram',
  'ad.notCounted': 'That ad did not count. You have {watched} of {total}, and the next one has to be watched in full.',
  // The human check (Cloudflare Turnstile) before a shift's ad, in the ad window.
  'captcha.title': 'Verification',
  'captcha.prompt': 'Confirm you are not a robot',
  'captcha.loading': 'Loading the check…',
  'captcha.checking': 'Checking…',
  'captcha.failed': 'That did not pass. Try again.',
  'captcha.expired': 'Time ran out. Try again.',
  'captcha.trouble': 'The check hit a snag. Try again.',
  'captcha.loadFailed': 'The check could not be loaded. Check your connection and try again.',
  'captcha.unsupported': 'This browser cannot show the check. Update Telegram and try again.',
  'captcha.noteMinutes': 'Needed to start shifts. It lasts {minutes} min, then it is asked again.',
  'captcha.noteSeconds': 'Needed to start shifts. It lasts {seconds} s, then it is asked again.',

  //---------------------------------------------------------------------------
  // Tasks
  //---------------------------------------------------------------------------
  'tasks.title': 'Tasks',
  'tasks.tab.all': 'All',
  'tasks.tab.daily': 'Daily',
  'tasks.tab.social': 'Social',
  'tasks.tab.partners': 'Partners',
  'tasks.tab.community': 'Community',
  'tasks.tabEmpty': 'No tasks here right now.',
  'tasks.heading': 'Complete tasks, earn Gems',
  'tasks.subheading': 'Complete more tasks and earn more Gems.',
  'tasks.daily': 'Daily',
  'tasks.extraMissions': 'Extra missions',
  'tasks.viewMissions': 'View missions',
  'tasks.specialReward': 'Special reward',
  'tasks.emptyTitle': 'No tasks available',
  'tasks.emptyBody': 'Check back soon — new tasks land regularly.',
  'tasks.action.go': 'Go',
  'tasks.action.check': 'Check',
  'tasks.action.checking': 'Checking…',
  'tasks.action.watchAds': 'Watch ads',
  'tasks.action.open': 'Open',
  'tasks.action.join': 'Join',
  'tasks.action.done': 'Done',
  'tasks.action.inReview': 'In review',

  //---------------------------------------------------------------------------
  // Daily check-in
  //---------------------------------------------------------------------------
  'checkin.title': 'Daily check-in',
  'checkin.subtitle': 'Sign in every day and collect your reward.',
  'checkin.day': 'Day {day}',
  'checkin.claim': 'Claim',
  'checkin.claiming': 'Claiming…',
  'checkin.adLoading': 'Loading ad…',
  'checkin.claimed': 'Claimed',
  'checkin.nextIn': 'Next in',
  'checkin.comeBackTomorrow': 'Come back tomorrow',
  'gigapub.title': 'GigaPub ads',
  'gigapub.desc': 'Watch an ad and earn {reward} Gems. Up to {limit} a day.',
  'gigapub.watch': 'Watch ad',
  'gigapub.loading': 'Loading…',
  'gigapub.playing': 'Watching…',
  'gigapub.wait': 'Wait',
  'gigapub.limitTitle': 'That’s all for today',
  'gigapub.limitBody': 'New ads at 00:00 UTC.',
  'gigapub.dismissedTitle': 'Ad closed early',
  'gigapub.dismissedBody': 'Watch it to the end to earn {reward} Gems.',
  'interstitial.title': 'AdsGram ads',
  'interstitial.desc': 'Keep each ad open {seconds} s to earn {reward} Gems. Up to {limit} a day.',
  'interstitial.watch': 'Watch ad',
  'interstitial.loading': 'Loading…',
  'interstitial.playing': 'Watching…',
  'interstitial.wait': 'Wait',
  'interstitial.limitTitle': 'That’s all for today',
  'interstitial.limitBody': 'New ads at 00:00 UTC.',
  'interstitial.earlyTitle': 'Ad closed too soon',
  'interstitial.earlyBody': 'Keep it open at least {seconds} s to earn {reward} Gems.',
  'adexium.title': 'Adexium ads',
  'adexium.desc': 'Keep each ad open {seconds} s to earn {reward} Gems. Up to {limit} a day.',
  'adexium.watch': 'Watch ad',
  'adexium.loading': 'Loading…',
  'adexium.playing': 'Watching…',
  'adexium.wait': 'Wait',
  'adexium.limitTitle': 'That’s all for today',
  'adexium.limitBody': 'New ads at 00:00 UTC.',
  'adexium.earlyTitle': 'Ad closed too soon',
  'adexium.earlyBody': 'Keep it open at least {seconds} s to earn {reward} Gems.',
  'usl.title': 'USL ads',
  'usl.desc': 'Watch an ad and earn {reward} Gems. Up to {limit} a day.',
  'usl.watch': 'Watch ad',
  'usl.loading': 'Loading…',
  'usl.playing': 'Watching…',
  'usl.wait': 'Wait',
  'usl.limitTitle': 'That’s all for today',
  'usl.limitBody': 'New ads at 00:00 UTC.',
  'usl.dismissedTitle': 'Ad closed early',
  'usl.dismissedBody': 'Watch it to the end to earn {reward} Gems.',

  //---------------------------------------------------------------------------
  // Daily combo
  //---------------------------------------------------------------------------
  'combo.title': 'Daily combo',
  'combo.clear': 'Clear',
  'combo.close': 'Close',
  'combo.solvedToast': 'Daily combo cracked!',
  /*
   * Split in two because the amount between them is rendered in bold. A
   * translated string is never allowed to carry markup: a translator is not a
   * developer, and `<b>` in a catalogue is a cross-site scripting bug waiting
   * for someone to render it with innerHTML.
   */
  'combo.solvedLead': 'Combo cracked!',
  'combo.solvedTail': 'are in your wallet. Come back tomorrow for the next one.',
  'combo.attemptsLeft_one': '{count} attempt left',
  'combo.attemptsLeft_other': '{count} attempts left',
  'combo.wrongPick': 'Not today’s combo',
  'combo.attemptsToday_one': 'You have {count} attempt left today.',
  'combo.attemptsToday_other': 'You have {count} attempts left today.',
  'combo.noneLeftToday': 'No attempts left today.',
  'combo.comeBackTomorrow': 'come back tomorrow',
  'combo.outOfAttempts': 'You are out of attempts today. The combo changes every day:',
  'combo.attemptsOf': 'You have {left} of {total} attempts left.',
  'swap.tooLittle': 'Too little to buy one {coin}',
  'combo.check': 'Check',
  'combo.checking': 'Checking…',
  'combo.claim': 'Claim',
  'combo.adLoading': 'Loading…',
  'combo.pending': 'Combo cracked! Watch a short ad to collect {reward}.',
  /*
   * The rules as the panel shows them, in plain words (no icons). {characters}
   * ("4 miners") and {reward} ("+250 Gems") are set in bold. The panel holds
   * four lines, so keep the sentence about this long. Its own wording: not the
   * combo rules other apps use ("collect … in the right order").
   */
  'combo.rules':
    'Today’s secret crew: {characters}, each in their spot. Get the clue in our channel and win {reward}.',
  'combo.rulesCharacters_one': '{count}\u00a0miner',
  'combo.rulesCharacters_other': '{count}\u00a0miners',
  'combo.nextIn': 'New combo in {time}',
  'combo.duration': '{h} H {m} M',
  'combo.slotFilled': 'Slot {n}: {name}',
  'combo.slotEmpty': 'Slot {n}: empty',

  //---------------------------------------------------------------------------
  // Roulette
  //---------------------------------------------------------------------------
  'roulette.title': 'Prize roulette',
  'roulette.close': 'Close',
  'roulette.spin': 'Spin',
  'roulette.spinning': 'Spinning…',
  'roulette.getMoreSpins': 'Get more spins',
  'roulette.viewRewards': 'View rewards',
  'roulette.rewards': 'Rewards',
  'roulette.rewardsLabel': 'Roulette rewards',
  'roulette.recentPrizes': 'Your latest prizes',
  'roulette.noSpinsYet': 'You have not spun the wheel yet.',
  'roulette.prize': 'Prize!',
  'roulette.spinsEach_one': '{count} spin',
  'roulette.spinsEach_other': '{count} spins',
  'roulette.spinsLeft_one': '{count} spin left',
  'roulette.spinsLeft_other': '{count} spins left',
  'roulette.yourSpins': 'Your spins',
  'roulette.getMore': 'Get more',
  'roulette.tagline': 'Spin and win epic prizes!',
  /*
   * The prizes the wheel names in words, one line per line on the wheel: \n is
   * where it breaks. The amounts (100 CRAFTGEM, 0.01 USDT) read the same in
   * every language and stay painted on the disc.
   */
  'roulette.wheel.keepTrying': 'Keep\ntrying',
  'roulette.wheel.epicMiner': 'Epic\nminer',
  'roulette.wheel.epicChest': 'Epic\nchest',
  'roulette.wheel.virtualFriends': 'Virtual\nfriends',
  'roulette.prizeSpins_one': '+{count} spin',
  'roulette.prizeSpins_other': '+{count} spins',
  'roulette.prizeEpicMiner': 'Epic miner: {name}',
  'roulette.prizeEpicChest': 'Epic chest: {amount} CRAFTGEM',
  'roulette.prizeVirtualFriend': 'Virtual friend: +1 active friend',

  //---------------------------------------------------------------------------
  // Invite
  //---------------------------------------------------------------------------
  'home.card.mining': 'Mining',
  'home.card.miningNote1': 'Start mining and',
  'home.card.miningNote2': 'earn Gems.',
  'home.card.invite': 'Invite friends',
  'home.card.inviteNote1': 'Invite friends and',
  'home.card.inviteNote2': 'earn more.',
  'home.card.wallet': 'Wallet',
  'home.card.walletNote1': 'View your balance',
  'home.card.walletNote2': 'and transactions.',
  'home.card.tasks': 'Tasks',
  'home.card.tasksNote1': 'Complete tasks',
  'home.card.tasksNote2': 'and earn rewards.',
  'invite.joinedOn': 'Joined {date}',
  'invite.earned': 'Earned',
  'invite.readingCrew': 'Reading your crew…',
  'invite.noInvitesYet': 'No invites yet',
  'invite.crewLoadFailed': 'Could not load your crew. Pull the screen down to try again.',
  'invite.shareToStart': 'Share your link or code — your rewards arrive when they reach level {level}.',
  'invite.status.active': 'Active',
  'invite.status.pending': 'Pending',
  'invite.status.rejected': 'Rejected',
  'invite.virtualFriend': 'Virtual friend',
  'invite.step1Name': 'Invite',
  'invite.step2Name': 'They join',
  'invite.step3Name': 'You earn',
  'invite.step1Note': 'Share your link or code\nwith your friends.',
  'invite.step2Note': 'Your friend signs up\nand mines to level {level}.',
  'invite.step3Note': 'At level {level} you get\n{spins} and {percent}%\nof all they earn.',
  'invite.kickerEarnMore': 'Earn more',
  'invite.kickerTogether': 'together',
  'invite.ledeGet': 'Earn',
  'invite.ledeOfEarnings': 'of everything your friends earn',
  'invite.ledeAnd': 'and',
  'invite.ledePerFriend': 'for each one who reaches level {level}.',
  'invite.spins_one': '{count} spin',
  'invite.spins_other': '{count} spins',
  'invite.yourReferralCode': 'Your referral code',
  'invite.copiedToClipboard': 'Copied to clipboard',
  'invite.copyLink': 'Copy invite link',
  'invite.totalCount': '{count} total',

  'invite.title': 'Invite friends',
  'invite.howItWorks': 'How it works',
  'invite.shareLink': 'Share invite link',
  'invite.friends': 'Friends',
  'invite.activeFriends': 'Active friends',
  'invite.invitedFriends': 'Invited friends',
  'invite.totalInvited': 'Total invited',
  'invite.totalEarned': 'Total earned',
  'invite.pending': 'Pending',
  'invite.rewards': 'Referral rewards',
  'invite.linkCopied': 'Invite link copied',

  //---------------------------------------------------------------------------
  // Wallet
  //---------------------------------------------------------------------------
  'wallet.title': 'Wallet',
  'wallet.availableBalance': 'Available balance',
  'wallet.earned': 'Earned',
  'wallet.withdrawn': 'Withdrawn',
  'wallet.deposit': 'Deposit',
  'wallet.withdraw': 'Withdraw',
  'wallet.swap': 'Swap',
  'wallet.link': 'Link',
  'wallet.history': 'History',
  'wallet.linkedWallets': 'Linked wallets',
  'wallet.noWalletLinked': 'No wallet linked',
  'wallet.tapLinkToAdd': 'Tap LINK to add one',
  'wallet.linkAWallet': 'Link a wallet',
  'wallet.walletLinked': 'Wallet linked',
  'wallet.withdrawalCancelled': 'Withdrawal cancelled',
  'wallet.gemsReturned': 'The Gems are back in your balance.',
  'wallet.withdrawalInProgress': 'Withdrawal in progress',
  'wallet.waitForThatOne': 'Wait for that one to finish before starting another.',

  //---------------------------------------------------------------------------
  // Withdraw
  //---------------------------------------------------------------------------
  'withdraw.maximum': 'Maximum is {amount}',
  'withdraw.minimumIs': 'Minimum is {amount}',
  'withdraw.linkFirst': 'Link a {coin} address first',
  'time.justNow': 'just now',
  'time.minutesAgo': '{count}m ago',
  'time.hoursAgo': '{count}h ago',
  'time.daysAgo': '{count}d ago',
  'deposit.memoField': 'Memo',
  'deposit.pendingSince': 'Pending · {when}',
  'withdraw.title': 'Withdraw',
  /* The detail sheet for one past withdrawal, not the action. */
  'withdraw.detailTitle': 'Withdrawal',
  'withdraw.request': 'Request withdrawal',
  'withdraw.confirm': 'Confirm Withdrawal',
  'withdraw.confirmLabel': 'Confirm withdrawal',
  'withdraw.withdrawTo': 'Withdraw to',
  'withdraw.youWithdraw': 'You withdraw',
  'withdraw.processingTime': 'Processing: 24-48h',
  'withdraw.secure': 'Secure and encrypted transaction.',
  'withdraw.notLinked': 'Not linked',
  'withdraw.requested': 'Requested',
  'withdraw.onTheWay': 'On the way to {address}',
  'withdraw.enterAmount': 'Enter how much to withdraw',
  'withdraw.invalidAmount': 'Enter a valid amount',
  'withdraw.overBalance': 'More than your available balance',
  'withdraw.belowMinimum': 'Below the minimum',
  'withdraw.assetClosed': 'That coin cannot be withdrawn right now',
  'withdraw.alreadyOpen': 'You already have a withdrawal in progress',
  'withdraw.networkFee': 'Network fee',
  'withdraw.destination': 'Destination',
  'withdraw.transaction': 'Transaction',
  'withdraw.completedAt': 'Completed',
  'withdraw.status.pending': 'Pending',
  'withdraw.status.processing': 'Processing',
  'withdraw.status.completed': 'Completed',
  'withdraw.status.rejected': 'Rejected',
  'withdraw.status.cancelled': 'Cancelled',
  'withdraw.cancelRequest': 'Cancel request',
  'withdraw.cancelling': 'Cancelling…',

  //---------------------------------------------------------------------------
  // Swap
  //---------------------------------------------------------------------------
  'swap.title': 'Swap Gems',
  'swap.amountToSwap': 'Amount to swap',
  'swap.youPay': 'You pay',
  'swap.youReceive': 'You receive',
  'swap.rate': 'Rate',
  'swap.rateLine': '{rate} Gems = 1 {coin}',
  'swap.ratePerUnit': '{rate} Gems / {coin}',
  'swap.readingRate': 'Reading the rate…',
  'swap.rateUnavailable': 'Rate unavailable',
  'swap.rateLoadFailed':
    'Could not load the rate. Close this and open it again — nothing has been swapped.',
  'swap.closed': 'Swapping is closed',
  'swap.closedBody': 'Swapping is closed right now. It opens from the operator panel.',
  'swap.enterAmount': 'Enter how many Gems to swap',
  'swap.invalidAmount': 'Enter a valid amount',
  'swap.overBalance': 'More than your available balance',
  'swap.minimum': 'Minimum is {amount} Gems',
  'swap.swapping': 'Swapping…',
  'swap.swapped': 'Swapped',
  'swap.swappedBody': '{amount} {coin} added to your balance.',
  'swap.swapTo': 'Swap to {coin}',
  'swap.available': 'Available: {amount} Gems',

  //---------------------------------------------------------------------------
  // Deposit
  //---------------------------------------------------------------------------
  'deposit.title': 'Deposit',
  'deposit.heading': 'Send to the address below, then tell us the transaction',
  'deposit.whatSending': 'What are you sending?',
  'deposit.sendToAddress': 'Send to this address',
  'deposit.memoLabel': 'Comment / memo (required)',
  'deposit.memoHint': "Put this in the transfer's comment field, or we cannot match it to you",
  'deposit.txHash': 'Transaction hash',
  'deposit.amountHint': 'Optional — it helps us find your transfer faster',
  'deposit.yourDeposits': 'Your deposits',
  'deposit.reported': 'Deposit reported',
  'deposit.reportedBody': 'It will be credited once the transfer is confirmed.',
  'deposit.hashTooShort': 'That hash looks too short',
  'deposit.invalidAmount': 'Enter a valid amount',
  'deposit.address': 'Address',
  'deposit.status.credited': 'Credited',
  'deposit.status.rejected': 'Rejected',
  'deposit.status.pending': 'Pending',
  'deposit.copyFailed': 'Could not copy',
  'deposit.clipboardDenied': 'Your browser would not give us the clipboard.',

  //---------------------------------------------------------------------------
  // Linked wallets
  //---------------------------------------------------------------------------
  'addresses.title': 'Connected wallets',
  'addresses.subtitle': 'Addresses you can pick when withdrawing',
  'addresses.walletAddress': 'Wallet address',
  'addresses.mustMatchNetwork': 'The address must match this network',
  'addresses.name': 'Name',
  'addresses.nameHint': 'Just for you, so you can tell them apart',
  'addresses.namePlaceholder': 'My main wallet',
  'addresses.save': 'Save wallet',
  'addresses.saved': 'Wallet saved',
  'addresses.remove': 'Remove',
  'addresses.removed': 'Wallet removed',
  'addresses.linked': 'Linked',
  'addresses.lockedHint': 'To change a wallet, contact support.',
  'addresses.linkOnce': 'One wallet per coin, linked only once. To change it later, contact support.',
  'addresses.pasteAddress': 'Paste an address',
  'addresses.nothingToPaste': 'Nothing to paste',
  'addresses.clipboardDenied': 'Your browser did not let the app read the clipboard.',
  'addresses.neverAsks': 'CraftGem never asks for a seed phrase or a private key.',
  'addresses.defaultLabel': 'Wallet',

  //---------------------------------------------------------------------------
  // Activity
  //---------------------------------------------------------------------------
  //---------------------------------------------------------------------------
  // The activity feed (§20)
  //
  // A row's words are written when it is created, hours or months before
  // anyone reads them, so the server stores these as keys beside the English
  // sentence. `titleKey`/`descriptionKey` on ActivityDto name one of these.
  //---------------------------------------------------------------------------
  'activity.shiftClaimed': '{miner} shift claimed',
  'activity.shiftDetail': '{minutes} min at {rate} Gems/h',
  'activity.checkin': 'Daily check-in',
  'activity.checkinDay': 'Day {day} reward',
  'activity.gigapub': 'GigaPub ad',
  'activity.gigapubCount': 'Ad {n} of {limit} today',
  'activity.interstitial': 'AdsGram ad',
  'activity.interstitialCount': 'Ad {n} of {limit} today',
  'activity.adexium': 'Adexium ad',
  'activity.adexiumCount': 'Ad {n} of {limit} today',
  'activity.usl': 'USL ad',
  'activity.uslCount': 'Ad {n} of {limit} today',
  'activity.combo': 'Daily combo',
  'activity.comboDetail': 'Four miners in the right order',
  'activity.roulette': 'Prize roulette',
  'activity.taskReward': 'Task reward',
  'activity.referralJoined': '{name} joined',
  'activity.referralMining': '{name} started mining',
  'activity.referralLevel': '{name} reached level {level}',
  'activity.referralSignupBonus': 'Referral bonus',
  'activity.referralActivationBonus': 'Activation bonus',
  'activity.levelReached': 'Reached level {level}',
  'activity.levelReward': 'Level reward',
  'activity.swapped': 'Swapped to {coin}',
  'activity.swapDetail': '{amount} {coin} at {rate} Gems per {coin}',
  'activity.withdrawal': 'Withdrawal to {network}',
  'activity.withdrawalDetail': '{net} Gems after a {fee} Gems fee',
  'activity.withdrawalDetailCoin': '{net} {coin} after a {fee} {coin} fee',
  'activity.adjustment': 'Balance adjustment',

  'activity.title': 'Activity',
  'activity.subtitle': 'Every reward, task and withdrawal',
  'activity.filter': 'Filter activity',
  'activity.filter.all': 'All',
  'activity.filter.mining': 'Mining',
  'activity.filter.tasks': 'Tasks',
  'activity.filter.referrals': 'Referrals',
  'activity.filter.withdrawals': 'Withdrawals',
  'activity.emptyTitle': 'Nothing here yet',
  'activity.emptyBody': 'Rewards and withdrawals will show up here.',
  'activity.status.success': 'Successful',
  'activity.empty.all': 'No activity yet. Your first claimed shift will show up here.',
  'activity.empty.mining': 'No mining rewards yet. Activate a miner to start a shift.',
  'activity.empty.tasks': 'No task rewards yet. The task centre has a few to get you started.',
  'activity.empty.referrals': 'No referral rewards yet. Share your link from the Invite tab.',
  'activity.empty.withdrawals': 'No withdrawals yet.',

  //---------------------------------------------------------------------------
  // Profile
  //---------------------------------------------------------------------------
  'rarity.common': 'Common',
  'rarity.rare': 'Rare',
  'rarity.epic': 'Epic',
  'rarity.legendary': 'Legendary',
  'profile.maxLevel': 'Max level',
  'profile.signingOut': 'Signing out…',
  'profile.endSession': 'End this session',
  'profile.statusActive': 'Active',
  'profile.how1':
    'Each miner on your crew runs one shift at a time. A shift is started by watching a rewarded ad — the ad is verified by our server before any shift opens, so nothing is granted on the client.',
  'profile.how2':
    'While a shift runs, Gems accrue at that miner’s rate. The amount is calculated from the server clock, not your device, so closing the app does not change what you earn.',
  'profile.how3':
    'When a shift ends the reward becomes claimable. Claiming credits your balance and writes an entry you can see in Activity. A reward can only ever be claimed once.',
  'profile.how4':
    'Tasks and referrals add Gems on top. Withdrawals move Gems out to a wallet you control, with the fee and final amount calculated on our side.',
  'profile.legal1':
    'CraftGem is a rewards application. Balances are earned through in-app activity and are not a security, an investment, or a promise of monetary value.',
  'profile.legal2':
    'Automating the app, falsifying ad completions, or creating accounts to farm referral rewards will result in the affected rewards being reversed and the account being suspended.',
  'profile.legal3':
    'We store the Telegram account id, username, display name and profile photo URL that Telegram provides when you open the Mini App, plus the activity you generate inside it. We also store the IP address you connect from, to allow one account per network and to prevent fraud.',
  'profile.legal4':
    'We never ask for a private key, a seed phrase, or a password. A withdrawal address is stored so the payout can be sent and so you can see where it went. Your data is not sold.',
  'profile.photoFromTelegram':
    'Your photo comes from Telegram',
  'profile.photoFromTelegramBody':
    'Change it in your Telegram profile and reopen CraftGem: it updates on the next sign-in.',
  'profile.nameFromTelegram':
    'Your name comes from Telegram',
  'profile.nameFromTelegramBody':
    'CraftGem shows the name on your Telegram account. Change it there and it follows.',

  'profile.title': 'Profile',
  'profile.miner': 'Miner',
  'profile.level': 'Lv.',
  'profile.nextLevel': 'Next level',
  'profile.logOut': 'Log out',
  'profile.copyId': 'Copy your ID',
  'profile.aboutYourName': 'About your name',
  'profile.backToHome': 'Back to home',
  'profile.row.walletAddress': 'Wallet address',
  'profile.row.notifications': 'Notifications',
  'profile.row.notificationsSub': 'Manage your notifications',
  'profile.row.language': 'Language',
  'profile.row.preferences': 'Preferences',
  'profile.row.preferencesSub': 'Haptics and app behaviour',
  'profile.row.security': 'Security',
  'profile.row.securitySub': 'Sessions and account safety',
  'profile.row.support': 'Support',
  'profile.row.supportSub': 'Help center and support',
  'profile.row.about': 'About',
  'profile.row.aboutSub': 'App version and legal information',
  'profile.row.accountStatus': 'Account status',
  'profile.row.accountStatusSub': 'Your account is in good standing.',
  'profile.row.addWallet': 'Add a wallet',
  'profile.sheet.notifications': 'What CraftGem may send you',
  'profile.sheet.preferences': 'How the app behaves on your phone',
  'profile.sheet.security': 'How this account is protected',
  'profile.sheet.support': 'How to reach us',
  'profile.sheet.about': 'What CraftGem is written in',
  'profile.sheet.how': 'Shifts, rewards and where the numbers come from',
  'profile.sheet.howTitle': 'How CraftGem works',
  'profile.sheet.language': 'Pick the language the app speaks',
  'profile.security.signOutAll': 'Sign out of every device',
  'profile.security.p1':
    'You sign in with Telegram, so there is no password here to steal. Every request carries a signature from Telegram that our server verifies, and the session it issues expires on its own.',
  'profile.security.p2':
    'Nothing can be moved without that session. Withdrawals go only to an address you type, are checked and paid on our side, and every one of them is listed in Activity.',
  'profile.security.p3':
    'We will never ask for a seed phrase, a private key or a password. If someone does, it is not us.',
  'profile.support.contact': 'Contact support on Telegram',
  'profile.support.p1':
    'Support answers on Telegram. Bring your ID — it is on this screen, under your name — and say what you were doing when it went wrong.',
  'profile.support.p2':
    'If your question is about how shifts, rewards or withdrawals work, the globe at the top of this screen explains all three and will probably be faster.',
  'profile.support.p3':
    'We will never ask you for a seed phrase, a private key or a password. If someone does, it is not us.',
  'profile.about.account': 'CraftGem · account #{id}',
  'profile.about.openActivity': 'Open activity history',
  'profile.notify.shiftFinished': 'Shift finished',
  'profile.notify.shiftFinishedSub': "When a miner's shift is ready to claim",
  'profile.notify.newTasks': 'New tasks',
  'profile.notify.newTasksSub': 'When a task is added',
  'profile.notify.referralActivity': 'Referral activity',
  'profile.notify.referralActivitySub': 'When someone you invited reaches level {level}',
  'profile.notify.checkin': 'Daily check-in',
  'profile.notify.checkinSub': 'When your daily check-in is available',
  'profile.notify.reminders': 'Pending tasks',
  'profile.notify.remindersSub': 'A reminder of the tasks you have not done yet',
  'profile.sound': 'Mine sound',
  'profile.soundSub': 'Picks, rock and the air of the shaft while a shift runs',
  'profile.volume': 'Volume',
  'profile.soundNote': 'The sound only plays while a miner is working, and never before you have touched the screen — that is the browser’s rule, not ours.',
  'profile.haptics': 'Haptic feedback',
  'profile.hapticsSub': 'Small vibrations when something lands',
  'profile.languageSaved': 'Language updated',

  //---------------------------------------------------------------------------
  // Toasts and notices
  //---------------------------------------------------------------------------
  //---------------------------------------------------------------------------
  // Database content: miner prose and task copy (§20)
  //
  // Keyed by the row's id, read through `maybe()`, and falling back to whatever
  // the database holds — so a miner or task an admin adds later shows the words
  // they typed rather than a missing key. A miner's `name` is not here: Grit
  // and Ember are names, not descriptions.
  //---------------------------------------------------------------------------
  'miner.grit.title': 'Tunnel Hand',
  'miner.grit.description': 'Steady, unhurried, never misses a shift. Grit works the shallow seams where the rock still gives easily.',
  'miner.ember.title': 'Vein Tracer',
  'miner.ember.description': 'Reads heat in the stone and follows it. Where Ember stops walking, there is always ore underneath.',
  'miner.slate.title': 'Shaft Foreman',
  'miner.slate.description': 'Runs the lower gallery by the book. Slate swings a sledge that opens rock two other miners would walk past.',
  'miner.quartz.title': 'Crystal Cutter',
  'miner.quartz.description': 'Chisel and mallet, never a pick. Quartz takes crystal out whole, and whole crystal is worth four times the dust.',
  'miner.onyx.title': 'Core Driller',
  'miner.onyx.description': 'Sealed rig, powered drill, no conversation. Onyx goes past the depth where the lamps stop helping.',
  'miner.aurum.title': 'Master Assayer',
  'miner.aurum.description': 'Does not dig. Aurum decides what is worth digging, and the crew has never once been wrong to listen.',
  'miner.prism.title': 'Vein Reader',
  'miner.prism.description': 'Reads a seam the way other miners read a map. Prism never swings twice at the same spot, because the first one already landed where it counted.',
  'miner.garnet.title': 'Deep Breaker',
  'miner.garnet.description': 'Works the level the signs warn you about. Garnet brings up the red crystal nobody else will go down for, and never explains why he keeps going back.',

  'task.tg-channel.title': 'Join the CraftGem channel',
  'task.tg-channel.description': 'Announcements, shift bonuses and payout notices. Membership is verified with Telegram.',
  'task.tg-group.title': 'Join the CraftGem crew chat',
  'task.tg-group.description': 'Where the crew compares shift output. Membership is verified with Telegram.',
  'task.first-shift.title': 'Complete your first shift',
  'task.first-shift.description': 'Activate a miner, let the shift run out, and claim what it produced.',
  'task.five-shifts.title': 'Claim five shifts',
  'task.five-shifts.description': 'Keep the mine running. Five claimed shifts unlocks a production bonus.',
  'task.two-miners.title': 'Run two miners at once',
  'task.two-miners.description': 'Have two shifts active at the same time.',
  'task.first-referral.title': 'Bring one miner to the crew',
  'task.first-referral.description': 'Invite someone with your referral link and have them mine to level {level}.',
  'task.x-follow.title': 'Follow CraftGem on X',
  'task.x-follow.description': 'Submitted for review. Off-platform follows cannot be verified automatically, so this one is checked by hand.',
  'task.tg-combo.title': 'Join the Daily Combo channel',
  'task.tg-combo.description': 'We post the winning combo every day. Use it and claim the daily prize.',

  'toast.shiftStarted': '{miner} is on shift',
  'toast.shiftStartedBody': 'The reward will be ready when the shift ends.',
  'toast.shiftClaimed': '{miner} finished the shift.',
  'toast.checkinDay': 'Daily check-in, day {day}',
  'toast.signedOut': 'Signed out',
  'toast.signedOutBody': 'Opening CraftGem again signs you back in with Telegram.',
  'toast.settingsSaved': 'Settings saved',
  'toast.idCopied': 'ID copied',
  'toast.copyFailed': 'Could not copy',
  'toast.clipboardDenied': 'Your browser would not give us the clipboard.',
  'toast.linkCopiedBody': 'Send it to anyone who should join your crew.',
  'toast.copyManually': 'Select the link and copy it manually.',
  'toast.copied': '{what} copied',
  'toast.noPrize': 'Keep trying',
  'toast.noPrizeBody': 'The wheel gave nothing this time.',
  'toast.noSpins': 'No spins left',
  'toast.noSpinsBody': 'Invite friends: each friend who reaches level {level} gives you {amount}.',

  //---------------------------------------------------------------------------
  // Errors, by application error code (§20, §24)
  //---------------------------------------------------------------------------
  'error.RATE_LIMITED.title': 'Slow down a moment',
  'error.RATE_LIMITED.body': 'Too many requests in a short time. Try again shortly.',
  'error.TOKEN_EXPIRED.title': 'Session expired',
  'error.TOKEN_EXPIRED.body': 'Reopen the app to sign back in.',
  'error.UNAUTHORIZED.title': 'Sign in to continue',
  'error.USER_SUSPENDED.title': 'Account suspended',
  'error.IP_BANNED.title': 'Network blocked',
  'error.IP_BANNED.body': 'This network is blocked. Connect from another network.',
  'error.ACCOUNT_UNDER_REVIEW.title': 'Account under review',
  'error.ACCOUNT_UNDER_REVIEW.body': 'Withdrawals are paused while we review your account. You can keep playing; contact support if you need help.',
  'ip.bannedTitle': 'Network blocked',
  'ip.bannedLead': 'Blocked network',
  'ip.bannedBody': 'This network (IP) is blocked on CraftGem.',
  'ip.bannedHint': 'Connect from another network — wifi or mobile data — to get in. If you think this is a mistake, contact support.',
  // One account per network (features/ban/IpInUseScreen.tsx).
  'error.IP_IN_USE.title': 'Network in use',
  'error.IP_IN_USE.body': 'Another account is already using this network. Change network to continue.',
  'ip.title': 'Network in use',
  'ip.lead': 'One account per network',
  'ip.body': 'Another CraftGem account is already using this network (IP). Only one account per network is allowed.',
  'ip.ruleLabel': 'How to get in',
  'ip.hint': 'Switch network — wifi or mobile data — or turn off your VPN, and try again. This network comes free {hours} hours after the other account stops using it.',
  'ip.retry': 'I changed network',
  'channel.title': 'Join the channel',
  'channel.lead': 'CraftGem is played from inside its community.',
  'channel.body': 'To use the app you need to be in our official Telegram channel. Join it, then tap “Check”.',
  'channel.ruleLabel': 'Official channel',
  'channel.join': 'Join channel',
  'channel.check': 'Check',
  'channel.titleGroup': 'Join the group',
  'channel.titleBoth': 'Join us',
  'channel.bodyGroup': 'To use the app you need to be in our official Telegram group. Join it, then tap “Check”.',
  'channel.bodyBoth': 'To use the app you need to be in our official Telegram channel and group. Join both, then tap “Check”.',
  'channel.groupLabel': 'Official group',
  'channel.joinGroup': 'Join group',
  'channel.done': 'Done',
  // One account per device: shown if a sign-in is refused for its device (server: modules/auth/devices.ts).
  'error.DEVICE_IN_USE.title': 'Device already in use',
  'error.DEVICE_IN_USE.body': 'This phone already has a CraftGem account. Only one account per device is allowed.',
  'error.DEVICE_REQUIRED.title': 'Reopen the app',
  'error.DEVICE_REQUIRED.body': 'Open CraftGem from Telegram and reload it to continue.',
  // The ban screen (features/ban/BannedScreen.tsx): one reason per group of the
  // panel's ban codes, and the facts of the ban.
  'ban.reason.SCRIPT.title': 'Use of scripts or bots',
  'ban.reason.SCRIPT.body': 'We detected automated activity on this account: scripts, bots or tools using the app in place of a person.',
  'ban.reason.REVIEW.title': 'Account under review',
  'ban.reason.REVIEW.body': 'We found unusual activity on this account. It stays on hold while our team reviews it.',
  'ban.reason.MULTI.title': 'Multiple accounts',
  'ban.reason.MULTI.body': 'This account is linked to other accounts run by the same person. Only one account per person is allowed.',
  'ban.reason.FAKE.title': 'Fake account',
  'ban.reason.FAKE.body': 'This account was created with false or borrowed details to collect rewards.',
  'ban.reason.FARM.title': 'Account farming',
  'ban.reason.FARM.body': 'This account is part of a group of accounts created or controlled to farm referral rewards.',
  'ban.reason.OTHER.title': 'Breach of the Terms',
  'ban.reason.OTHER.body': 'This account broke the Terms and Conditions of CraftGem.',
  'ban.rule': 'Rule broken · Terms and Conditions, point {point}',
  'ban.code': 'Code',
  'ban.date': 'Suspended on',
  'ban.duration': 'Duration',
  'ban.permanent': 'Permanent',
  'ban.reference': 'Reference',
  'ban.support': 'Contact support',
  'ban.supportHint': 'Give support your reference so they can review your case.',
  'ban.terms': 'Terms and Conditions',
  'ban.termsHide': 'Hide the terms',
  'error.USER_SUSPENDED.body': 'Contact support from your profile.',
  'error.MINER_LOCKED.title': 'Still locked',
  'error.MINER_LOCKED.body': 'Meet the requirement to bring this miner onto your crew.',
  'error.MINER_ALREADY_WORKING.title': 'Already on shift',
  'error.MINER_ALREADY_WORKING.body': 'This miner is working right now.',
  'error.MINER_CLAIM_PENDING.title': 'Claim first',
  'error.MINER_CLAIM_PENDING.body': "Collect this miner's reward before starting a new shift.",
  'error.SHIFT_NOT_READY.title': 'Shift still running',
  'error.SHIFT_NOT_READY.body': 'Wait for the shift to finish, then claim.',
  'error.SHIFT_ALREADY_CLAIMED.title': 'Already claimed',
  'error.SHIFT_ALREADY_CLAIMED.body': 'This reward has already landed in your balance.',
  'error.NOTHING_TO_CLAIM.title': 'Nothing to claim yet',
  'error.AD_VERIFICATION_FAILED.title': 'Ad not verified',
  'error.AD_VERIFICATION_FAILED.body': 'We could not confirm that ad. Try again.',
  'error.AD_SESSION_EXPIRED.title': 'Ad session expired',
  'error.AD_SESSION_EXPIRED.body': 'Start the ad again.',
  'error.AD_NONCE_MISMATCH.title': 'Ad could not be verified',
  'error.AD_EVENT_ALREADY_CONSUMED.title': 'Already used',
  'error.AD_EVENT_ALREADY_CONSUMED.body': 'That ad reward has already been applied.',
  'error.AD_TOO_FAST.title': 'Ad finished too quickly',
  'error.AD_TOO_FAST.body': 'Watch the full ad to activate a shift.',
  'error.AD_PROVIDER_UNAVAILABLE.title': 'No ads available',
  'error.AD_PROVIDER_UNAVAILABLE.body': 'Try again in a few minutes.',
  'error.AD_PROVIDER_UNAVAILABLE.noSdk': 'Ads are not set up in this version of the app.',
  'error.AD_PROVIDER_UNAVAILABLE.noBlock': 'Ads are not set up on the server yet.',
  'error.AD_PROVIDER_UNAVAILABLE.notLoaded': 'The ad network did not load.',
  'error.AD_PROVIDER_UNAVAILABLE.unreachable': 'The ad network could not be reached.',
  'error.AD_PROVIDER_UNAVAILABLE.noFill': 'There is no ad to show right now. Try again in a few minutes.',
  'error.AD_CONFIRMATION_PENDING.title': 'Confirming the ad',
  'error.AD_CONFIRMATION_PENDING.body':
    'The ad network has not confirmed it yet. Try again in a moment.',
  'error.AD_DAILY_LIMIT.title': 'Daily ad limit reached',
  'error.AD_DAILY_LIMIT.body': 'Come back tomorrow to start more shifts.',
  'error.AD_DISMISSED.title': 'Ad closed early',
  'error.AD_DISMISSED.body': 'The full ad has to play to activate a shift.',
  'error.AD_TOO_SOON.title': 'Too many ads in a row',
  'error.AD_TOO_SOON.body': 'Wait a few seconds and try again. Nothing was lost.',
  'error.CAPTCHA_REQUIRED.title': 'Confirm you are not a robot',
  'error.CAPTCHA_REQUIRED.body': 'Starting a shift needs the human check. Try again.',
  'error.CAPTCHA_FAILED.title': 'Check not passed',
  'error.CAPTCHA_FAILED.body': 'The human check did not pass. Try again.',
  'error.CAPTCHA_UNAVAILABLE.title': 'Check unavailable',
  'error.CAPTCHA_UNAVAILABLE.body': 'The human check could not be reached. Try again in a moment.',
  'error.TASK_VERIFICATION_FAILED.title': 'Not verified yet',
  'error.TASK_VERIFICATION_FAILED.body': 'Complete the task, then check again.',
  'error.TASK_ALREADY_COMPLETED.title': 'Already completed',
  'error.TASK_DWELL_TOO_SHORT.title': 'Give it a moment',
  'error.TASK_DWELL_TOO_SHORT.body': 'Spend a little longer on the task before checking.',
  'error.TASK_PENDING_REVIEW.title': 'Submitted for review',
  'error.TASK_PENDING_REVIEW.body': 'The reward lands in your activity once confirmed.',
  'error.CHECKIN_ALREADY_CLAIMED.title': 'Already claimed today',
  'error.CHECKIN_ALREADY_CLAIMED.body': 'Come back tomorrow for the next day.',
  'error.COMBO_ALREADY_SOLVED.title': 'Already cracked today',
  'error.COMBO_ALREADY_SOLVED.body': 'A new combo opens tomorrow.',
  'error.AD_REQUIRED.title': 'Watch the ad first',
  'error.AD_REQUIRED.body': 'This reward is collected after a short ad.',
  'error.AD_COOLDOWN.title': 'Wait a moment',
  'error.AD_COOLDOWN.body': 'The next ad will be ready in {seconds} s.',
  'error.AD_SLOT_OFF.title': 'Ads unavailable',
  'error.AD_SLOT_OFF.body': 'These ads are not available right now.',
  'error.COMBO_NOT_SOLVED.title': 'Crack the combo first',
  'error.COMBO_NOT_SOLVED.body': 'The reward opens once you guess today’s four miners.',
  'error.COMBO_NO_ATTEMPTS.title': 'No attempts left',
  'error.COMBO_NO_ATTEMPTS.body': 'The combo changes every day. Try again tomorrow.',
  'error.COMBO_INVALID_PICK.title': 'Pick four miners',
  'error.COMBO_INVALID_PICK.body': 'Choose four different miners, in order.',
  'error.INSUFFICIENT_BALANCE.title': 'Not enough Gems',
  'error.WITHDRAWAL_ALREADY_OPEN.title': 'Withdrawal in progress',
  'error.WITHDRAWAL_ALREADY_OPEN.body': 'Wait for it to finish before starting another.',
  'error.WITHDRAWAL_COOLDOWN.title': 'Withdrawal limit',
  'error.WITHDRAWAL_COOLDOWN.body': 'You can withdraw once every {hours} h. Your next withdrawal opens in {wait}.',
  'error.WITHDRAWAL_MINERS_REQUIRED.title': 'More miners needed',
  'error.WITHDRAWAL_MINERS_REQUIRED.body': 'To withdraw you need {required} miners working at the same time. Working now: {active}.',
  'error.AD_NOT_CLICKED.title': 'The ad did not count',
  'error.AD_NOT_CLICKED.body': 'Tap the button inside the ad while it plays — only then does it count.',
  'error.AD_CLICK_TOO_SHORT.title': 'Too quick',
  'error.AD_CLICK_TOO_SHORT.body': 'Stay at least {seconds} seconds in the ad after tapping its button. This one did not count.',
  'error.CHANNEL_REQUIRED.title': 'Join the official channel',
  'error.CHANNEL_REQUIRED.body': 'To use the app you need to be in our official channel.',
  'error.BAD_REQUEST.title': 'Something is off',
  'error.BAD_REQUEST.body': 'That could not be done. Try again.',
  'error.FORBIDDEN.title': 'Not allowed',
  'error.FORBIDDEN.body': 'You cannot do that.',
  'error.NOT_FOUND.title': 'Not found',
  'error.NOT_FOUND.body': 'That is no longer here.',
  'error.CONFLICT.title': 'It changed meanwhile',
  'error.CONFLICT.body': 'Something changed in the meantime. Try again.',
  'error.INVALID_INIT_DATA.title': 'Could not sign you in',
  'error.INVALID_INIT_DATA.body': 'Open the app again from Telegram.',
  'error.INIT_DATA_EXPIRED.title': 'Session expired',
  'error.INIT_DATA_EXPIRED.body': 'Close the app and open it again from Telegram.',
  'error.MINER_NOT_FOUND.title': 'Miner not found',
  'error.MINER_NOT_FOUND.body': 'That miner is not in your crew.',
  'error.SHIFT_NOT_FOUND.title': 'Shift not found',
  'error.SHIFT_NOT_FOUND.body': 'That shift no longer exists.',
  'error.AD_SESSION_NOT_FOUND.title': 'Ad not found',
  'error.AD_SESSION_NOT_FOUND.body': 'That ad has ended. Watch another one.',
  'error.TASK_NOT_FOUND.title': 'Task not found',
  'error.TASK_NOT_FOUND.body': 'That task is no longer available.',
  'error.TASK_NOT_AVAILABLE.title': 'Task unavailable',
  'error.TASK_NOT_AVAILABLE.body': 'This task cannot be done right now.',
  'error.TASK_NOT_STARTED.title': 'Start the task first',
  'error.TASK_NOT_STARTED.body': 'Open the task, do it, then come back to check it.',
  'error.ROULETTE_NO_SPINS.title': 'No spins left',
  'error.ROULETTE_NO_SPINS.body': 'You have no spins right now.',
  'error.REFERRAL_CODE_INVALID.title': 'Invalid code',
  'error.REFERRAL_CODE_INVALID.body': 'That invite code does not exist.',
  'error.REFERRAL_SELF.title': 'That is your own code',
  'error.REFERRAL_SELF.body': 'You cannot invite yourself.',
  'error.REFERRAL_ALREADY_SET.title': 'Already invited',
  'error.REFERRAL_ALREADY_SET.body': 'Your account already has someone who invited you.',
  'error.WITHDRAWAL_NOT_FOUND.title': 'Withdrawal not found',
  'error.WITHDRAWAL_NOT_FOUND.body': 'That withdrawal no longer exists.',
  'error.WITHDRAWAL_NOT_CANCELLABLE.title': 'Cannot be cancelled',
  'error.WITHDRAWAL_NOT_CANCELLABLE.body': 'This withdrawal is already being processed.',
  'error.SWAP_DISABLED.title': 'Swap closed',
  'error.SWAP_DISABLED.body': 'Swapping is not available right now.',
  'error.SWAP_BELOW_MINIMUM.title': 'Below the minimum',
  'error.SWAP_BELOW_MINIMUM.body': 'Swap at least the minimum amount shown.',
  'error.SWAP_TOO_SMALL.title': 'Amount too small',
  'error.SWAP_TOO_SMALL.body': 'That amount is worth less than the smallest unit of the coin.',
  'error.SWAP_ASSET_UNKNOWN.title': 'Coin not available',
  'error.SWAP_ASSET_UNKNOWN.body': 'That coin cannot be swapped into.',
  'error.DEPOSIT_UNAVAILABLE.title': 'Deposits closed',
  'error.DEPOSIT_UNAVAILABLE.body': 'Deposits are not available right now.',
  'error.DEPOSIT_DUPLICATE.title': 'Already reported',
  'error.DEPOSIT_DUPLICATE.body': 'That transaction was already reported.',
  'error.DEPOSIT_NOT_FOUND.title': 'Deposit not found',
  'error.DEPOSIT_NOT_FOUND.body': 'That deposit no longer exists.',
  'error.ADDRESS_NOT_FOUND.title': 'Wallet not found',
  'error.ADDRESS_NOT_FOUND.body': 'That wallet is not saved.',
  'error.INVALID_ADDRESS.title': 'Check the address',
  'error.WITHDRAWAL_BELOW_MINIMUM.title': 'Below the minimum',
  'error.WITHDRAWAL_ABOVE_MAXIMUM.title': 'Above the maximum',
  'error.WITHDRAWALS_PAUSED.title': 'Withdrawals paused',
  'error.WITHDRAWALS_PAUSED.body': 'They are paused for maintenance. Try again later.',
  'error.UNSUPPORTED_NETWORK.title': 'Network unavailable',
  'error.UNSUPPORTED_NETWORK.body': 'That network is not available right now.',
  'error.UNSUPPORTED_ASSET.title': 'Coin unavailable',
  'error.ADDRESS_LIMIT_REACHED.title': 'Too many saved wallets',
  'error.ADDRESS_LOCKED.title': 'Wallet already linked',
  'error.ADDRESS_LOCKED.body': 'A wallet can only be linked once. To change it, contact support.',
  'error.DEPOSIT_TOO_MANY_PENDING.title': 'Too many open deposits',
  'error.DEPOSIT_TOO_MANY_PENDING.body': 'Wait for your pending deposits to be reviewed first.',
  'error.IDEMPOTENCY_KEY_REUSED.title': 'Duplicate request',
  'error.REQUEST_IN_FLIGHT.title': 'Already working on it',
  'error.REQUEST_IN_FLIGHT.body': 'That request is still being processed.',
  'error.MAINTENANCE_MODE.title': 'Under maintenance',
  'error.MAINTENANCE_MODE.body': 'This is paused right now. Try again shortly.',
  'error.SERVICE_UNAVAILABLE.title': 'Service unavailable',
  'error.SERVICE_UNAVAILABLE.body': 'Try again in a moment.',
  'error.VALIDATION_FAILED.title': 'Check those values',
  'error.INTERNAL_ERROR.title': 'Something went wrong',
  'error.INTERNAL_ERROR.body': 'Please try again in a moment.',
} as const;

export type Catalog = Record<keyof typeof EN, string>;
export type CatalogKey = keyof typeof EN;

/** The six categories `Intl.PluralRules` can return, across all languages. */
type PluralCategory = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';

/**
 * The base name of every key that has plural variants.
 *
 * Exported because it is what `plural()` takes: the base is what a caller
 * names, and the category is chosen at runtime. The base itself is deliberately
 * *not* a key in the catalogue — there is no one sentence for "some number of
 * attempts" — so typing `plural()` as `CatalogKey` was wrong.
 */
export type PluralBase = CatalogKey extends infer K
  ? K extends `${infer Base}_${PluralCategory}`
    ? Base
    : never
  : never;

/**
 * What a locale file may declare.
 *
 * Not `Partial<Catalog>`: Arabic has `zero`, `two`, `few` and `many` forms and
 * Russian has `few` and `many`, none of which English declares — so a type
 * built from the English keys alone would reject exactly the translations that
 * need them most. This allows any category for a key that *is* a plural family
 * in English, and still rejects a misspelled base name, which is the mistake
 * worth catching.
 */
export type LocaleCatalog = Partial<
  Record<CatalogKey | `${PluralBase}_${PluralCategory}`, string>
>;

/** Every key, for the audit script and for tests. */
export const CATALOG_KEYS = Object.keys(EN) as CatalogKey[];
